import { Injectable, inject, signal } from '@angular/core';
import {
  AnimationSlot,
  FrameRenderData,
  SpriteAnimationConfig,
  resolveSheet,
} from '../types/animation';
import { AnimationSpeedService } from './animation-speed.service';

/** Frames advance every frameDurationMs (~100ms+) while loops tick at ~16ms, so most ticks
 *  change nothing — callers gate their signal writes on frameChanged. */
export function advanceSlot(slot: AnimationSlot, deltaTime: number): { completed: boolean; frameChanged: boolean } {
  const prevFrameIndex = slot.currentFrameIndex;
  slot.elapsedTime += deltaTime;
  const { frames, frameDurationMs, loop } = slot.config;
  const totalFrames = frames.length;
  const frameIndex = Math.floor(slot.elapsedTime / frameDurationMs);

  let completed = false;
  if (frameIndex >= totalFrames) {
    if (loop) {
      slot.elapsedTime %= totalFrames * frameDurationMs;
      slot.currentFrameIndex = Math.floor(slot.elapsedTime / frameDurationMs);
    } else {
      slot.currentFrameIndex = totalFrames - 1;
      if (!slot.hasCompleted) {
        slot.hasCompleted = true;
        slot.config.onComplete?.();
        completed = true;
      }
    }
  } else {
    slot.currentFrameIndex = frameIndex;
  }
  return { completed, frameChanged: slot.currentFrameIndex !== prevFrameIndex };
}

export function computeFrameData(slot: AnimationSlot | null): FrameRenderData | null {
  if (!slot) return null;
  const frame = slot.config.frames[slot.currentFrameIndex];
  if (!frame) return null;

  return {
    image: resolveSheet(slot.image),
    sourceX: frame[0],
    sourceY: frame[1],
    sourceWidth: slot.config.frameWidth,
    sourceHeight: slot.config.frameHeight,
  };
}

/** Receives the raw (unscaled) frame delta and reports whether it still has work. */
export type FrameTicker = (rawDeltaMs: number) => boolean;

// Battle-free sprite animation: the spritesheet cache plus the app's single self-stopping rAF
// loop, driving component-registered ("dynamic") slots and any external ticker. Kept separate
// from SpriteAnimationService so eager consumers (mon-static-sprite on home/lobby) don't pull
// the battle/engine graph into main.js.
@Injectable({ providedIn: 'root' })
export class FrameLoopService {
  private readonly animSpeed = inject(AnimationSpeedService);

  private readonly spritesheetCache = new Map<string, HTMLImageElement>();
  private readonly pendingLoads = new Map<string, Promise<HTMLImageElement>>();
  // Bumped as sheets land so reactive sheet() reads re-run once the image is decodable.
  private readonly sheetsLoaded = signal(0);

  private readonly dynamicSlots = new Map<string, AnimationSlot>();
  private readonly dynamicSlotSignals = new Map<string, ReturnType<typeof signal<FrameRenderData | null>>>();

  private readonly tickers = new Set<FrameTicker>();
  private frameId: number | null = null;
  private lastTimestamp: number | null = null;
  private isRunning = false;

  /** Join the shared loop. Re-adding a live ticker is how a caller wakes a stopped loop,
   *  so this is idempotent; the ticker scales the raw delta itself. */
  addTicker(ticker: FrameTicker): void {
    this.tickers.add(ticker);
    this.ensureLoopRunning();
  }

  removeTicker(ticker: FrameTicker): void {
    this.tickers.delete(ticker);
  }

  registerAnimation(config: SpriteAnimationConfig): {
    id: string;
    frame: ReturnType<typeof signal<FrameRenderData | null>>;
  } {
    const id = crypto.randomUUID();
    const frameSignal = signal<FrameRenderData | null>(null);
    this.dynamicSlotSignals.set(id, frameSignal);

    this.createAnimationSlot(config)
      .then((slot) => {
        // Unregistered while the sheet loaded: a looping slot added now would never be removed.
        if (!this.dynamicSlotSignals.has(id)) return;
        this.dynamicSlots.set(id, slot);
        this.ensureLoopRunning();
      })
      .catch((e) => console.error('[sprite] spritesheet load failed', config.spritesheetUrl, e));

    return { id, frame: frameSignal };
  }

  unregisterAnimation(id: string): void {
    this.dynamicSlots.delete(id);
    this.dynamicSlotSignals.delete(id);
  }

  preloadSpritesheet(url: string): void {
    void this.loadSpritesheet(url).catch(() => {});
  }

  /** Reactive handle on a cached sheet, for callers that paint a frame themselves instead of
   *  owning a slot. Starts the load on first ask; returns null until it lands. */
  sheet(url: string): HTMLImageElement | null {
    this.sheetsLoaded();
    const cached = this.spritesheetCache.get(url);
    if (cached) return cached;
    void this.loadSpritesheet(url)
      .then(() => this.sheetsLoaded.update((n) => n + 1))
      .catch(() => {});
    return null;
  }

  async createAnimationSlot(config: SpriteAnimationConfig): Promise<AnimationSlot> {
    const image = await this.loadSpritesheet(config.spritesheetUrl);
    return { config, image, elapsedTime: 0, currentFrameIndex: 0, hasCompleted: false };
  }

  private async loadSpritesheet(url: string): Promise<HTMLImageElement> {
    const cached = this.spritesheetCache.get(url);
    if (cached) return cached;

    const pending = this.pendingLoads.get(url);
    if (pending) return pending;

    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.spritesheetCache.set(url, img);
        this.pendingLoads.delete(url);
        resolve(img);
      };
      img.onerror = () => {
        this.pendingLoads.delete(url);
        reject(new Error(`Failed to load spritesheet: ${url}`));
      };
      img.src = url;
    });

    this.pendingLoads.set(url, loadPromise);
    return loadPromise;
  }

  private ensureLoopRunning(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastTimestamp = null;
    this.frameId = requestAnimationFrame((ts) => this.tick(ts));
  }

  private stopLoop(): void {
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    this.isRunning = false;
    this.lastTimestamp = null;
  }

  private tick(timestamp: number): void {
    if (!this.isRunning) return;

    // Clamped so a hidden-tab resume doesn't deliver the whole hidden span as one delta
    // (snap-completing one-shots and skipping loop frames).
    const rawDelta = this.lastTimestamp !== null ? Math.min(timestamp - this.lastTimestamp, 250) : 0;
    this.lastTimestamp = timestamp;

    // Dynamic slots always tick at 1x (battle speed is in-battle only).
    // Slots mutate in place; each per-slot signal emits only when its visible frame moves
    // (the first emit is forced by lastEmittedFrameIndex starting undefined).
    // Completed one-shots stay registered (their signal keeps the last frame) but are inert:
    // nothing revives them in place, so they must not keep the loop alive.
    const skip = this.animSpeed.skipAnimations();
    let slotsActive = false;
    for (const [id, slot] of this.dynamicSlots) {
      const inert = slot.hasCompleted && !slot.config.loop;
      if (!inert && !(skip && slot.config.freezeOnSkip)) {
        advanceSlot(slot, rawDelta);
      }
      if (slot.currentFrameIndex !== slot.lastEmittedFrameIndex) {
        slot.lastEmittedFrameIndex = slot.currentFrameIndex;
        this.dynamicSlotSignals.get(id)?.set(computeFrameData(slot));
      }
      if (!inert) slotsActive = true;
    }

    // One ticker throwing must not take the shared loop (and every other sprite) down with it.
    let tickersActive = false;
    for (const ticker of this.tickers) {
      try {
        if (ticker(rawDelta)) tickersActive = true;
      } catch (e) {
        console.error('[sprite] frame ticker failed', e);
      }
    }

    if (slotsActive || tickersActive) {
      this.frameId = requestAnimationFrame((ts) => this.tick(ts));
    } else {
      this.stopLoop();
    }
  }
}
